"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/contexts/CartContext";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";

const navLinks = [
  { label: "Home", href: "/homepage" },
  { label: "Shop", href: "/products" },
  { label: "Checkout", href: "/checkout" },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const { cartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-dreamy py-2"
          : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2 group min-h-[44px]">
          <AppLogo
            size={64}
            text="My Happy Place"
            iconName="BookOpenIcon"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className={`nav-link-underline text-sm font-semibold transition-colors duration-200 min-h-[44px] flex items-center ${
                pathname === link?.href
                  ? "text-plum active-nav" :"text-plum/70 hover:text-plum"
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://wa.me/256764590167"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs font-bold text-green-600 bg-green-50 border border-green-200 rounded-full px-3 py-2 hover:bg-green-100 transition-colors active:scale-95 min-h-[44px]"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={16} variant="solid" />
            WhatsApp
          </a>
          <Link
            href="/cart"
            className="relative p-3 rounded-full bg-plum-light/10 hover:bg-plum-light/20 transition-all duration-300 active:scale-95 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={`Shopping cart with ${cartCount} items`}
          >
            <Icon name="ShoppingCartIcon" size={20} variant="outline" className="text-plum" />
            <AnimatePresence>
              {cartCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 bg-rose text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg"
                >
                  {cartCount > 99 ? "99+" : cartCount}
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
          <Link
            href="/products"
            className="btn-primary text-sm px-5 py-2.5 flex items-center gap-2 active:scale-95 min-h-[44px]"
          >
            <Icon name="ShoppingBagIcon" size={16} variant="solid" />
            Shop Now
          </Link>
        </div>

        {/* Mobile menu toggle */}
        <button
          className="md:hidden p-3 rounded-xl bg-white/80 border border-lavender text-plum active:scale-95 min-h-[44px] min-w-[44px] flex items-center justify-center"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          <Icon name={menuOpen ? "XMarkIcon" : "Bars3Icon"} size={24} />
        </button>
      </div>
      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-lavender/30 shadow-dreamy-lg px-6 py-6 flex flex-col gap-3">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              onClick={() => setMenuOpen(false)}
              className={`text-base font-semibold py-3 border-b border-lavender/20 min-h-[48px] flex items-center ${
                pathname === link?.href ? "text-plum" : "text-plum/70"
              }`}
            >
              {link?.label}
            </Link>
          ))}
          <Link
            href="/cart"
            onClick={() => setMenuOpen(false)}
            className="flex items-center justify-between text-base font-semibold py-3 border-b border-lavender/20 text-plum min-h-[48px]"
          >
            <span className="flex items-center gap-2">
              <Icon name="ShoppingCartIcon" size={20} variant="outline" />
              Cart
            </span>
            {cartCount > 0 && (
              <span className="bg-rose text-white text-xs font-bold rounded-full px-2.5 py-1">
                {cartCount}
              </span>
            )}
          </Link>
          <a
            href="https://wa.me/256764590167"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-base font-bold text-green-600 py-3 min-h-[48px]"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
            Chat on WhatsApp
          </a>
          <Link
            href="/products"
            onClick={() => setMenuOpen(false)}
            className="btn-primary text-center text-base px-6 py-4 mt-2 min-h-[48px]"
          >
            Shop Now ✨
          </Link>
        </div>
      )}
    </header>
  );
}